import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import dayjs from 'dayjs';

// Redux-Actions
import {
  getInterviewerAvailiblityByFilterAction,
  // getInterviewersListBySearchNameAction,
  getInterviewTechnology
} from '../../redux/actions/hr';

// Utils
import {
  formatExperience,
  getUserRole,
  isDarkMode,
  normalizeObject,
  wordCapitalize
} from '../../utils/common';
import { formatTime, getFormattedDate, getFormattedDateView } from '../../utils/date';

// Constants
import {
  DEBOUNCE_TIMEOUT,
  INITIAL_PAGE_NUMBER,
  INITIAL_SIZE,
  INTERVIEWER_SCHEDULE_FILTER_KEY,
  TABLE_NAME
} from '../../constants/common';
import DataTable from '../../components/DataTable';
import { HR_INTERVIEWERS_COLUMNS } from '../../constants/tableColumns';
import { debounce } from '../../utils/debounce';
import UserDetails from '../../components/popup/UserDetails';
import { userDetailsAction } from '../../redux/actions/userDetails';
import {
  INTERVIEWER_FILTER_DROPDOWN_OPTIONS,
  INTERVIEWERS_DATE_FILTER_DROPDOWN_OPTIONS
} from '../../constants/dropdownOptions';
import { Chip } from '@mui/material';
import CopyToClipboard from '../../components/CopyToClipboard';

const initialDateValue = dayjs();

const interviewersTableStates = {
  pageNumber: INITIAL_PAGE_NUMBER,
  size: INITIAL_SIZE,
  sortBy: 'DateModified',
  orderBy: 'desc',
  searchName: '',
  startDateFilter: null,
  technology: '',
  practicalOptionFilter: null
};

const Schedule = () => {
  // Redux hooks
  const dispatch = useDispatch();
  const { pagination } = useSelector((state) => state.hrs);
  const interviewerAvailiblityList = useSelector((state) => state.hrs.interviewerAvailiblityList);
  const technologyListData = useSelector((state) => state.hrs.interviewTechnologyType);
  const { userDetails } = useSelector((state) => state);

  // React useState hooks
  const [interviewerList, setInterviewerList] = useState([]);
  const [interviewerDetails, setInterviewerDetails] = useState([]);
  const [interviewerId, setInterviewerId] = useState(null);
  // const [handleSearchAndFilter, setHandleSearchAndFilter] = useState(interviewersTableStates);
  const calendarRef = useRef();
  const [showPopup, setShowPopup] = useState(false);
  const onTogglePopup = () => setShowPopup((prevState) => !prevState);

  const normalizeTableState = (state) => {
    if (!state) return interviewersTableStates;

    return {
      ...interviewersTableStates,
      ...state,
      technology: state.technology || '',
      practicalOptionFilter: state.practicalOptionFilter || null,
      startDateFilter: state.startDateFilter || null,
      endDateFilter: state.endDateFilter || null
    };
  };

  const getSavedFilters = () => {
    try {
      const saved = localStorage.getItem(INTERVIEWER_SCHEDULE_FILTER_KEY);
      return saved ? JSON.parse(saved) : null;
    } catch {
      return null;
    }
  };

  const [handleSearchAndFilter, setHandleSearchAndFilter] = useState(() => {
    const saved = getSavedFilters();
    return saved ? normalizeTableState(saved) : interviewersTableStates;
  });

  useEffect(() => {
    const { searchName, startDateFilter, technology, practicalOptionFilter } =
      handleSearchAndFilter;

    const isFilterApplied =
      searchName?.trim() !== '' ||
      startDateFilter !== null ||
      (Array.isArray(technology) ? technology.length > 0 : technology?.trim() !== '') ||
      practicalOptionFilter !== null;

    if (isFilterApplied) {
      localStorage.setItem(INTERVIEWER_SCHEDULE_FILTER_KEY, JSON.stringify(handleSearchAndFilter));
    } else {
      localStorage.removeItem(INTERVIEWER_SCHEDULE_FILTER_KEY);
    }
  }, [handleSearchAndFilter]);

  // React useEffect hooks
  useEffect(() => {
    if (interviewerAvailiblityList && interviewerAvailiblityList.length) {
      const setData = interviewerAvailiblityList?.map((interviewer) => {
        const availabilityOfInterviewer = interviewer?.InterviewerToAvailability?.map(
          (availability) => {
            return {
              ...availability,
              title: `${wordCapitalize(interviewer?.FirstName)} ${wordCapitalize(
                interviewer?.LastName
              )}`,
              start: `${getFormattedDate(initialDateValue)} ${
                availability.StartTimeOfAvailability || ''
              }`,
              end: `${getFormattedDate(initialDateValue)} ${
                availability.EndTimeOfAvailability || ''
              }`,
              interviewerId: interviewer?.ID,
              url: `/hr/interviews?interviewId=${interviewer?.ID}&startDate=${getFormattedDate(
                initialDateValue
              )}&startTime=${availability.StartTimeOfAvailability}&technology=`
            };
          }
        );
        return availabilityOfInterviewer;
      });

      let availabilityList = [];
      setData.map((list) => {
        if (list.length) {
          availabilityList.push(...list);
        }
      });
    }
  }, [interviewerAvailiblityList]);

  useEffect(() => {
    interviewerId && dispatch(userDetailsAction(interviewerId));
  }, [dispatch, interviewerId]);

  const modifyUserDetails = useCallback(() => {
    if (userDetails?.ID) {
      setInterviewerDetails([
        {
          title: 'Name',
          value: `${wordCapitalize(userDetails?.FirstName || '')} ${wordCapitalize(
            userDetails?.LastName || ''
          )}`
        },
        { title: 'Role', value: getUserRole(userDetails?.UserTypeId || '') },
        {
          title: 'Gender',
          value: userDetails?.Gender == 1 ? 'Male' : userDetails?.Gender == 2 ? 'Female' : '--'
        },
        {
          title: 'Skills',
          value: userDetails?.UMap?.length
            ? userDetails?.UMap?.map((tech) => wordCapitalize(tech?.MapT?.Technology)).join(' | ')
            : ''
        },
        {
          title: 'Experience',
          value: formatExperience(userDetails?.YearsOfExperience, userDetails?.MonthsOfExperience)
        },
        {
          title: 'Will take Practical Round?',
          value: userDetails.IsPracticalAllow ? 'Yes' : 'No'
        },
        {
          title: 'Is Admin of Practical Team?',
          value: userDetails.IsPracticalAdmin ? 'Yes' : 'No'
        },
        {
          title: 'Email Address',
          value: (
            <div className="center">
              <span>{userDetails?.Email || '--'}</span>
              {userDetails?.Email && <CopyToClipboard text={userDetails?.Email} />}
            </div>
          )
        },
        {
          title: `PM's Email Address`,
          value: (
            <div className="center">
              <span>{userDetails?.ProjectManagerEmail || '--'}</span>
              {userDetails?.ProjectManagerEmail && (
                <CopyToClipboard text={userDetails?.ProjectManagerEmail} />
              )}
            </div>
          )
        },
        {
          title: 'Mobile',
          value: (
            <div className="center">
              <span>{userDetails?.Mobile || '--'}</span>
              {userDetails?.Mobile && <CopyToClipboard text={userDetails?.Mobile} />}
            </div>
          )
        },
        {
          title: 'Create on',
          value:
            userDetails?.DateCreated &&
            `${getFormattedDateView(userDetails?.DateCreated)} ${formatTime(
              userDetails?.DateCreated
            )} `
        },
        {
          title: 'Status',
          value: `${
            userDetails?.UserStatus ? (userDetails.UserStatus === 1 ? 'Active' : 'Inactive') : ''
          }${
            userDetails?.UserStatusUpdatedAt
              ? ` since ${getFormattedDateView(userDetails.UserStatusUpdatedAt)} ${formatTime(
                  userDetails.UserStatusUpdatedAt
                )}`
              : ''
          }`
        }
      ]);
    }
  }, [userDetails]);

  useEffect(() => {
    modifyUserDetails();
  }, [userDetails, modifyUserDetails]);

  // Call an API on component mount only to get the list of technologies.
  useEffect(() => {
    dispatch(getInterviewTechnology());
  }, [dispatch]);

  useEffect(() => {
    dispatch(getInterviewerAvailiblityByFilterAction(handleSearchAndFilter));
  }, [initialDateValue, dispatch, handleSearchAndFilter]);

  const handleView = (ID) => {
    if (ID) {
      onTogglePopup();
      setInterviewerId(ID);
    }
  };

  useEffect(() => {
    const modifiedCandidate = interviewerAvailiblityList?.map((list, index) => {
      return {
        ...list,
        Name: (
          <span className="table-view-popup-link" onClick={() => handleView(list.ID)}>
            {`${wordCapitalize(list.FirstName)} ${wordCapitalize(list.LastName)}`}
          </span>
        ),
        Technology: (
          <ul className="technology-list" key={index}>
            {list.UMap && list?.UMap?.length > 0
              ? list.UMap?.map((tech) => (
                  <li
                    key={tech?.MapT?.ID}
                    className="technology-badge"
                    style={{
                      backgroundColor: `${
                        isDarkMode() ? tech?.MapT?.DarkModeColor : tech?.MapT?.TechnologyColor
                      }`
                    }}>
                    {tech?.MapT?.Technology}
                  </li>
                ))
              : []}
          </ul>
        ),
        Practical: (
          <Chip
            color={list?.IsPracticalAllow ? 'success' : 'error'}
            label={list?.IsPracticalAllow ? 'Yes' : 'No'}
          />
        ),
        PracticalAdmin: (
          <Chip
            color={list?.IsPracticalAdmin ? 'success' : 'error'}
            label={list?.IsPracticalAdmin ? 'Yes' : 'No'}
          />
        ),
        'Interviews Taken': list?.InvitationsCount || '--',
        Experience: formatExperience(list?.YearsOfExperience, list?.MonthsOfExperience)
      };
    });

    setInterviewerList(modifiedCandidate);
  }, [interviewerAvailiblityList, isDarkMode()]);

  useEffect(() => {
    if (calendarRef?.current && initialDateValue) {
      let calendarApi = calendarRef.current.getApi();
      calendarApi.gotoDate(initialDateValue);
    }
  }, [initialDateValue]);

  const handlePagination = (pageNumber, size) =>
    setHandleSearchAndFilter((prevState) => {
      return {
        ...prevState,
        pageNumber,
        size
      };
    });

  const handleSearch = (searchValue) => {
    setHandleSearchAndFilter({
      ...handleSearchAndFilter,
      searchName: searchValue
    });
  };

  const handleSort = (sortBy) => {
    setHandleSearchAndFilter((prevState) => {
      return {
        ...prevState,
        sortBy,
        orderBy: prevState.orderBy === 'asc' ? 'desc' : 'asc'
      };
    });
  };

  const handleStatusFilter = (value) => {
    setHandleSearchAndFilter({
      ...handleSearchAndFilter,
      technology: Array.isArray(value) ? value : value ? [value] : []
    });
  };

  const handleStartDateFilter = (value) => {
    let startDate = null;
    let endDate = null;

    switch (value) {
      case 'LAST_15DAY':
        startDate = dayjs().subtract(15, 'day').format('YYYY-MM-DD');
        endDate = dayjs().format('YYYY-MM-DD');
        break;
      case 'LAST_30DAY':
        startDate = dayjs().subtract(30, 'day').format('YYYY-MM-DD');
        endDate = dayjs().format('YYYY-MM-DD');
        break;
      case 'THIS_WEEK':
        startDate = dayjs().startOf('week').format('YYYY-MM-DD');
        endDate = dayjs().format('YYYY-MM-DD');
        break;
      case 'LAST_WEEK':
        startDate = dayjs().subtract(1, 'week').startOf('week').format('YYYY-MM-DD');
        endDate = dayjs().subtract(1, 'week').endOf('week').format('YYYY-MM-DD');
        break;
      case 'THIS_MONTH':
        startDate = dayjs().startOf('month').format('YYYY-MM-DD');
        endDate = dayjs().format('YYYY-MM-DD');
        break;
      case 'LAST_MONTH':
        startDate = dayjs().subtract(1, 'month').startOf('month').format('YYYY-MM-DD');
        endDate = dayjs().subtract(1, 'month').endOf('month').format('YYYY-MM-DD');
        break;
      default:
        startDate = null;
        endDate = null;
    }

    setHandleSearchAndFilter({
      ...handleSearchAndFilter,
      dateFilterType: value || null,
      startDateFilter: startDate,
      endDateFilter: endDate
    });
  };

  const handlePracticalOptionFilter = (value) => {
    setHandleSearchAndFilter({
      ...handleSearchAndFilter,
      practicalOptionFilter: value || null
    });
  };

  useEffect(() => {
    const totalPages = Math.ceil(pagination?.totalRecords / handleSearchAndFilter.size);

    if (handleSearchAndFilter.pageNumber > totalPages && totalPages > 0) {
      setHandleSearchAndFilter((prev) => ({
        ...prev,
        pageNumber: 1
      }));
    }
  }, [pagination?.totalRecords, handleSearchAndFilter.pageNumber, handleSearchAndFilter.size]);

  return (
    <>
      <UserDetails
        header="Interviewer Details"
        data={interviewerDetails}
        onHide={onTogglePopup}
        show={showPopup}
      />
      <DataTable
        name={TABLE_NAME.INTERVIEWERS}
        totalRecords={pagination?.totalRecords}
        onPagination={handlePagination}
        onSearch={debounce(handleSearch, DEBOUNCE_TIMEOUT)}
        searchPlaceHolder="Search by Interviewer name, Email and Mobile"
        onSort={handleSort}
        data={interviewerList}
        isDropdownMultiSelect
        dropdown={
          technologyListData?.length
            ? technologyListData.map((tech) => ({ name: tech.Technology, value: tech.ID }))
            : []
        }
        searchValue={handleSearchAndFilter.searchName}
        dropdownValue={handleSearchAndFilter.technology}
        onSecondDropdownChange={handleStartDateFilter}
        secondDropdown={INTERVIEWERS_DATE_FILTER_DROPDOWN_OPTIONS || []}
        secondDropdownPlaceHolder="Select Status"
        onStatusFilter={handleStatusFilter}
        // secondDropdownValue={handleSearchAndFilter.startDateFilter}
        secondDropdownValue={handleSearchAndFilter.dateFilterType}
        pageNumber={handleSearchAndFilter.pageNumber}
        pageSize={handleSearchAndFilter.size}
        dropdownPlaceHolder="Select Technology"
        columns={HR_INTERVIEWERS_COLUMNS}
        clearFilter
        thirdDropdown={INTERVIEWER_FILTER_DROPDOWN_OPTIONS || []}
        thirdDropdownValue={handleSearchAndFilter.practicalOptionFilter}
        onThirdDropdownChange={handlePracticalOptionFilter}
        isThirdDropdownMultiSelect
        isClearFilterDisabled={
          JSON.stringify(normalizeObject(interviewersTableStates)) ===
          JSON.stringify(normalizeObject(handleSearchAndFilter))
        }
        clearFilterOnClick={() => {
          setHandleSearchAndFilter(interviewersTableStates);
          localStorage.removeItem(INTERVIEWER_SCHEDULE_FILTER_KEY);
        }}
      />
    </>
  );
};

export default Schedule;
